"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { List } from "lucide-react";

type Heading = {
  id: string;
  text: string;
  level: number;
};

export function TableOfContents({ content }: { content: string }) {
  const [activeId, setActiveId] = useState<string>("");
  const listRef = useRef<HTMLUListElement>(null);
  const isClickScrolling = useRef(false);
  const isClient = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const headings = useMemo(() => {
    if (!isClient) return [];

    const article = document.querySelector(".blog-content");
    if (!article) return [];
    const els = article.querySelectorAll("h2, h3");
    return Array.from(els).map((el) => ({
      id: el.id,
      text: el.textContent ?? "",
      level: el.tagName === "H2" ? 2 : 3,
    }));
  }, [isClient]);

  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -80% 0px" },
    );

    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [headings]);

  useEffect(() => {
    if (!activeId || !listRef.current) return;
    const list = listRef.current;
    const activeLink = list.querySelector<HTMLElement>(
      `a[href="#${CSS.escape(activeId)}"]`,
    );
    if (!activeLink) return;
    const targetScrollTop =
      activeLink.offsetTop -
      list.offsetTop -
      list.clientHeight / 2 +
      activeLink.clientHeight / 2;
    list.scrollTo({ top: targetScrollTop, behavior: "smooth" });
  }, [activeId]);

  if (!isClient || headings.length < 3) return null;

  return (
    <nav className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/60">
        <List className="h-4 w-4" />
        Contenido
      </h3>
      <ul
        ref={listRef}
        className="max-h-[calc(100vh-10rem)] space-y-2 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/10"
      >
        {headings.map((heading) => (
          <li
            key={heading.id}
            style={{ paddingLeft: `${(heading.level - 2) * 12}px` }}
          >
            <a
              href={`#${heading.id}`}
              onClick={(e) => {
                e.preventDefault();
                isClickScrolling.current = true;
                setActiveId(heading.id);
                document.getElementById(heading.id)?.scrollIntoView({ behavior: "smooth" });
                setTimeout(() => { isClickScrolling.current = false; }, 800);
              }}
              className={`block text-sm transition-colors ${
                activeId === heading.id
                  ? "font-medium text-red-400"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
